import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { CheckCircle2, ArrowRight } from "lucide-react";
import NavBar from "../components/NavBar";
import Footer from "../components/Footer";
import FloatingWhatsApp from "../components/FloatingWhatsApp";
import { CmsImageSlideshow as CmsImage } from "../lib/images";
import { api } from "../lib/api";
import { useI18n } from "../i18n";
import { waProps, waLabel } from "../lib/whatsapp";

/**
 * Centro de Monitoreo — página editorial premium siguiendo el mismo patrón
 * cinematográfico del resto del site (XEGA VIP, Merch Finder, E-Tracker,
 * Sentinel, Seguridad Privada). Estructura:
 *   1. Hero banner + título cromado centrado ("CENTRO DE MONITOREO")
 *   2. Bloque editorial: párrafo introductorio + lista de capacidades +
 *      imagen slideshow (izquierda)
 *   3. Bloque editorial: imagen slideshow (derecha) + micro-frase
 *   4. CTA metálico con botón WhatsApp
 */
export default function MonitoringCenter() {
  const { pick } = useI18n();
  const [content, setContent] = useState(null);

  useEffect(() => {
    api.get("/content").then((r) => setContent(r.data?.data || null)).catch(() => {});
    api.post("/track/visit", { path: "/centro-monitoreo" }).catch(() => {});
  }, []);

  // Verbatim intro — traducido a EN / PT manteniendo el mismo tono formal
  const INTRO_ES = "Nuestro CENTRO DE MONITOREO cuenta con la infraestructura y tecnologías más avanzadas en el mundo, permitiéndonos ofrecer los servicios de seguridad que se requieren ante los actuales niveles de inseguridad, basados en uno de los mejores y más avanzados Centros de Monitoreo en América Latina para:";
  const INTRO_EN = "Our MONITORING CENTER features the world's most advanced infrastructure and technology, allowing us to deliver the security services required by today's threat levels — powered by one of Latin America's leading Monitoring Centers, purpose-built for:";
  const INTRO_PT = "Nossa CENTRAL DE MONITORAMENTO conta com a infraestrutura e as tecnologias mais avançadas do mundo, permitindo-nos oferecer os serviços de segurança exigidos pelos atuais níveis de insegurança, apoiada em uma das melhores e mais avançadas Centrais de Monitoramento da América Latina para:";

  const BULLETS = [
    { es: "Localización de personas, vehículos y mercancía.",
      en: "Location of people, vehicles and merchandise.",
      pt: "Localização de pessoas, veículos e mercadorias." },
    { es: "Recepción de alarmas personales con ubicación.",
      en: "Personal alarm reception with location.",
      pt: "Recepção de alarmes pessoais com localização." },
    { es: "Botones de emergencia inalámbricos o en dispositivos móviles.",
      en: "Wireless emergency buttons or on mobile devices.",
      pt: "Botões de emergência sem fio ou em dispositivos móveis." },
    { es: "Los mejores mapas de América Latina.",
      en: "The best maps in Latin America.",
      pt: "Os melhores mapas da América Latina." },
    { es: "Sistemas totalmente ocultos en mercancía y vehículos.",
      en: "Fully hidden systems inside merchandise and vehicles.",
      pt: "Sistemas totalmente ocultos em mercadorias e veículos." },
    { es: "Rastreos y localizaciones en tiempo real.",
      en: "Real-time tracking and location.",
      pt: "Rastreamentos e localizações em tempo real." },
    { es: "Vehículos con batería o sin batería propia.",
      en: "Vehicles with their own battery or without one.",
      pt: "Veículos com bateria própria ou sem bateria própria." },
  ];

  return (
    <div className="min-h-screen text-white" data-testid="monitoring-page">
      <NavBar />

      {/* ═══════ HERO ═══════ */}
      <section className="relative overflow-hidden h-[62vh] min-h-[440px] max-h-[720px] bg-[#06080D]">
        <CmsImage
          slotKey="monitoring.hero"
          defaultSrc="/monitoring/hero.jpg"
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover object-center opacity-75"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#06080D] via-[#06080D]/70 to-[#06080D]/20" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#06080D] via-transparent to-transparent" />
        <div className="absolute inset-0 grid-bg opacity-[0.08]" />

        {/* Centered chrome title */}
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="absolute inset-0 flex items-center justify-center pointer-events-none px-4"
          data-testid="monitoring-banner-title"
        >
          <span
            className="font-display chrome-text text-4xl sm:text-6xl md:text-7xl lg:text-[6rem] tracking-[0.06em] leading-none text-center whitespace-nowrap"
            style={{ filter: 'drop-shadow(0 4px 24px rgba(0,0,0,0.85))' }}
          >
            {pick("CENTRO DE MONITOREO", "MONITORING CENTER", "CENTRAL DE MONITORAMENTO")}
          </span>
        </motion.h1>

        <div className="absolute bottom-0 inset-x-0 h-10 bg-gradient-to-b from-transparent to-[#06080D] pointer-events-none" />
      </section>

      {/* ═══════ BLOCK #1 — Intro + bullets + left image ═══════ */}
      <section className="relative py-4 md:py-8 bg-[#06080D] lg:-mt-24 relative z-10">
        <div className="absolute top-1/3 -left-40 w-[560px] h-[560px] rounded-full bg-cyan-500/8 blur-[160px] pointer-events-none" />
        <div className="max-w-7xl mx-auto px-5 sm:px-6 md:px-10">
          <div className="grid lg:grid-cols-12 gap-8 lg:gap-14 items-start">
            {/* Left image with chrome frame */}
            <motion.div
              initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
              transition={{ duration: .7 }}
              className="lg:col-span-5 relative"
            >
              <div className="relative rounded-2xl overflow-hidden chrome-border shadow-[0_25px_60px_-15px_rgba(0,240,255,0.15)]">
                <CmsImage
                  slotKey="monitoring.image1"
                  defaultSrc="/monitoring/analyst.jpg"
                  alt=""
                  className="w-full h-full object-cover aspect-[4/3]"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-tr from-[#06080D]/40 via-transparent to-cyan-500/10 mix-blend-multiply pointer-events-none" />
              </div>
              <div className="absolute -top-2 -left-2 w-6 h-6 border-t border-l border-cyan-400/40 pointer-events-none" />
              <div className="absolute -bottom-2 -right-2 w-6 h-6 border-b border-r border-cyan-400/40 pointer-events-none" />
            </motion.div>

            {/* Right column: intro + bullets */}
            <motion.div
              initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ duration: .7, delay: .1 }}
              className="lg:col-span-7"
            >
              <p className="text-white/85 text-base sm:text-lg leading-relaxed">
                {pick(INTRO_ES, INTRO_EN, INTRO_PT)}
              </p>

              <ul className="mt-6 space-y-3">
                {BULLETS.map((b, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-cyan-300 shrink-0 mt-0.5" />
                    <span className="text-white/85 text-base leading-relaxed">
                      {pick(b.es, b.en, b.pt)}
                    </span>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ═══════ BLOCK #2 — Right image with tagline ═══════ */}
      <section className="relative py-4 md:py-8 bg-[#06080D]">
        <div className="absolute bottom-1/3 -right-40 w-[560px] h-[560px] rounded-full bg-emerald-400/6 blur-[160px] pointer-events-none" />
        <div className="max-w-7xl mx-auto px-5 sm:px-6 md:px-10">
          <div className="grid lg:grid-cols-12 gap-8 lg:gap-14 items-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ duration: .7 }}
              className="lg:col-span-6 order-2 lg:order-1"
            >
              <p className="text-white/85 text-base sm:text-lg leading-relaxed">
                {pick(
                  "Operadores altamente capacitados trabajan las 24 horas, respaldados por sistemas redundantes y protocolos de reacción validados en campo. Cada alerta es atendida por un humano — no un algoritmo — y coordinada con las autoridades correspondientes cuando la situación lo requiere.",
                  "Highly-trained operators work around the clock, backed by redundant systems and field-tested response protocols. Every alert is handled by a human — not an algorithm — and coordinated with the proper authorities when the situation demands it.",
                  "Operadores altamente capacitados trabalham 24 horas, apoiados por sistemas redundantes e protocolos de reação validados em campo. Cada alerta é atendido por um humano — não um algoritmo — e coordenado com as autoridades competentes quando necessário."
                )}
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
              transition={{ duration: .7, delay: .1 }}
              className="lg:col-span-6 order-1 lg:order-2 relative"
            >
              <div className="relative rounded-2xl overflow-hidden chrome-border shadow-[0_25px_60px_-15px_rgba(0,240,255,0.15)]">
                <CmsImage
                  slotKey="monitoring.image2"
                  defaultSrc="/monitoring/control-room.jpg"
                  alt=""
                  className="w-full h-full object-cover aspect-[4/3]"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-tl from-[#06080D]/45 via-transparent to-emerald-500/10 mix-blend-multiply pointer-events-none" />
              </div>
              <div className="absolute -top-2 -right-2 w-6 h-6 border-t border-r border-cyan-400/40 pointer-events-none" />
              <div className="absolute -bottom-2 -left-2 w-6 h-6 border-b border-l border-cyan-400/40 pointer-events-none" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* ═══════ CTA ═══════ */}
      <section className="relative py-6 md:py-10 bg-[#06080D]">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 md:px-10">
          <div className="relative overflow-hidden rounded-3xl chrome-border p-8 sm:p-10 md:p-14">
            <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/10 via-transparent to-emerald-500/10 pointer-events-none" />
            <div
              aria-hidden="true"
              className="pointer-events-none absolute -top-24 left-1/2 -translate-x-1/2 w-[520px] h-[520px] rounded-full"
              style={{ background: 'radial-gradient(circle, rgba(0,240,255,0.10) 0%, transparent 65%)', filter: 'blur(20px)' }}
            />
            <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-8 md:gap-10">
              <motion.h3
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ duration: 0.7 }}
                className="relative font-display leading-[1.08] tracking-tight max-w-2xl text-[1.6rem] sm:text-3xl md:text-4xl lg:text-[2.6rem] self-center"
                data-testid="monitoring-cta-headline"
              >
                <span
                  className="chrome-text italic font-semibold"
                  style={{
                    letterSpacing: '-0.005em',
                    filter: 'drop-shadow(0 2px 12px rgba(0,240,255,0.15)) drop-shadow(0 1px 0 rgba(0,0,0,0.6))',
                  }}
                >
                  {pick(
                    "Hablemos del monitoreo que tu operación necesita.",
                    "Let's talk about the monitoring your operation needs.",
                    "Vamos falar sobre o monitoramento que sua operação precisa."
                  )}
                </span>
              </motion.h3>
              <a
                {...waProps(content?.contact?.whatsapp ? `https://wa.me/${String(content.contact.whatsapp).replace(/[^0-9]/g,'')}?text=${encodeURIComponent(pick('Hola, quiero información sobre el Centro de Monitoreo','Hi, I want information about the Monitoring Center','Olá, quero informação sobre a Central de Monitoramento'))}` : "#contact")}
                className="shrink-0 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r from-cyan-400 to-emerald-400 text-black font-tech uppercase tracking-wider text-sm shadow-[0_0_28px_-4px_rgba(0,240,255,0.7)] hover:shadow-[0_0_36px_-4px_rgba(0,240,255,0.95)] transition self-start md:self-auto"
                data-testid="monitoring-cta-whatsapp"
              >
                {waLabel(pick, "Escribir por WhatsApp", "Message on WhatsApp", "Falar pelo WhatsApp")}
                <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer content={content} />
      <FloatingWhatsApp phone={content?.contact?.whatsapp} />
    </div>
  );
}
